import {
  Button,
  Card,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Tooltip,
} from "@fluentui/react-components";
import {
  MoreVertical20Regular,
  OpenRegular,
  ShareRegular,
} from "@fluentui/react-icons";
import { Constants, ConstantValues } from "common/constants";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { copyTextToClipboard } from "utils/generalUtils";
import { ArchiveIcon } from "../../../icons/ArchiveIcon";
import { DocumentAddIcon } from "../../../icons/DocumentAddIcon";
import { DocumentIcon } from "../../../icons/DocumentIcon";
import { EditIcon } from "../../../icons/EditIcon";
import { EyeIcon } from "../../../icons/EyeIcon";
import { LeaveIcon } from "../../../icons/LeaveIcon";
import { LockIcon } from "../../../icons/LockIcon";
import { TrashIcon } from "../../../icons/TrashIcon";
import { UserAddIcon } from "../../../icons/UserAddIcon";
import { UsersIcon } from "../../../icons/UsersIcon";
import { showToast } from "../../../redux/toastActions";
import {
  useSharedCardDetailStyles,
  useSharedMenuStyles,
} from "../../../theme/sharedStyles";
import { useStyles } from "./WorkspaceCard.styles";
import {
  IWorkspaceCardProps,
  MENU_LABELS,
  TOAST_MESSAGES,
  TOOLTIP_MESSAGES,
  WORKSPACE_CARD_CONSTANTS,
} from "./WorkspaceCard.types";

export const WorkspaceCard = (props: IWorkspaceCardProps) => {
  const {
    workspace,
    onEdit,
    onDelete,
    onArchive,
    onUnarchive,
    onLeave,
    onSeeDetails,
    onEnable,
    onAddDocuments,
    onAddMember,
  } = props;

  const styles = useStyles();
  const cardDetailStyles = useSharedCardDetailStyles();
  const menuStyles = useSharedMenuStyles();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const [menuOpen, setMenuOpen] = useState(false);

  const isPersonalWorkspace =
    workspace.name === ConstantValues.PERSONAL_WORKSPACE;
  const isOwner = workspace.owners?.some(
    (owner) => owner.userId === currentUser.id,
  );
  const isPlatformAdmin = currentUser.roles.includes(
    Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
  );
  const isArchived = workspace.archived;
  const canEdit =
    (isOwner || isPlatformAdmin) && !isPersonalWorkspace && !isArchived;

  const owners = workspace.owners ?? [];
  const nonOwnerMembers = (workspace.teamMembers ?? []).filter(
    (m) => !owners.some((o) => o.userId === m.userId),
  );
  const membersList =
    owners.length === 0 && nonOwnerMembers.length === 0 ? (
      "No members"
    ) : (
      <div>
        {owners.length > 0 && (
          <div>
            <strong>Owners:</strong>
            {owners.map((o) => (
              <div key={o.userId}>{o.displayName}</div>
            ))}
          </div>
        )}
        {nonOwnerMembers.length > 0 && (
          <div>
            <strong>Members:</strong>
            {nonOwnerMembers.map((m) => (
              <div key={m.userId}>{m.displayName}</div>
            ))}
          </div>
        )}
      </div>
    );

  const copyDirectLink = async () => {
    const url = `${window.location.origin.replace(/\/$/, "")}/workspace/${workspace.id}`;
    try {
      await copyTextToClipboard(url);
      dispatch(showToast(TOAST_MESSAGES.LINK_COPIED, "success"));
    } catch {
      dispatch(showToast(TOAST_MESSAGES.LINK_COPY_FAILED, "error"));
    }
  };

  const handleUseWorkspace = () => {
    if (!isArchived) {
      onEnable(workspace);
    }
  };

  const renderContextMenu = () => {
    if (isPersonalWorkspace) return null;

    if (isArchived) {
      return (
        <Menu
          open={menuOpen}
          onOpenChange={(_, data) => setMenuOpen(data.open)}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              icon={<MoreVertical20Regular />}
              appearance="subtle"
              size="small"
              className={styles.moreButton}
              aria-label={`More options for ${workspace.name}`}
            />
          </MenuTrigger>
          <MenuPopover className={menuStyles.surface}>
            <MenuList>
              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <EyeIcon size={16} />
                  </span>
                }
                onClick={() => onSeeDetails(workspace)}
              >
                {MENU_LABELS.DETAILS}
              </MenuItem>

              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <ArchiveIcon size={16} />
                  </span>
                }
                onClick={() => onUnarchive(workspace)}
              >
                {MENU_LABELS.UNARCHIVE}
              </MenuItem>

              <MenuItem
                icon={
                  <span className={menuStyles.itemIconDanger}>
                    <TrashIcon size={16} />
                  </span>
                }
                className={menuStyles.itemDanger}
                onClick={() => onDelete(workspace)}
              >
                {MENU_LABELS.DELETE}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }

    if (!isOwner) {
      return (
        <Menu
          open={menuOpen}
          onOpenChange={(_, data) => setMenuOpen(data.open)}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              icon={<MoreVertical20Regular />}
              appearance="subtle"
              size="small"
              className={styles.moreButton}
              aria-label={`More options for ${workspace.name}`}
            />
          </MenuTrigger>
          <MenuPopover className={menuStyles.surface}>
            <MenuList>
              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <EyeIcon size={16} />
                  </span>
                }
                onClick={() => onSeeDetails(workspace)}
              >
                {MENU_LABELS.DETAILS}
              </MenuItem>

              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <DocumentAddIcon size={16} />
                  </span>
                }
                onClick={() => onAddDocuments(workspace)}
              >
                {MENU_LABELS.ADD_DOCUMENTS}
              </MenuItem>

              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <UserAddIcon size={16} />
                  </span>
                }
                onClick={() => onAddMember(workspace)}
              >
                {MENU_LABELS.ADD_MEMBER}
              </MenuItem>

              <MenuItem
                icon={
                  <span className={menuStyles.itemIconDanger}>
                    <LeaveIcon size={16} />
                  </span>
                }
                className={menuStyles.itemDanger}
                onClick={() => onLeave(workspace)}
              >
                {MENU_LABELS.LEAVE_WORKSPACE}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }

    return (
      <Menu open={menuOpen} onOpenChange={(_, data) => setMenuOpen(data.open)}>
        <MenuTrigger disableButtonEnhancement>
          <Button
            icon={<MoreVertical20Regular />}
            appearance="subtle"
            size="small"
            className={styles.moreButton}
            aria-label={`More options for ${workspace.name}`}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {canEdit && (
              <MenuItem
                icon={
                  <span className={menuStyles.itemIcon}>
                    <EditIcon size={16} />
                  </span>
                }
                onClick={() => onEdit(workspace)}
              >
                {MENU_LABELS.EDIT}
              </MenuItem>
            )}

            <MenuItem
              icon={
                <span className={menuStyles.itemIcon}>
                  <EyeIcon size={16} />
                </span>
              }
              onClick={() => onSeeDetails(workspace)}
            >
              {MENU_LABELS.DETAILS}
            </MenuItem>

            <MenuItem
              icon={
                <span className={menuStyles.itemIcon}>
                  <DocumentAddIcon size={16} />
                </span>
              }
              onClick={() => onAddDocuments(workspace)}
            >
              {MENU_LABELS.ADD_DOCUMENTS}
            </MenuItem>

            <MenuItem
              icon={
                <span className={menuStyles.itemIcon}>
                  <ArchiveIcon size={16} />
                </span>
              }
              onClick={() => onArchive(workspace)}
            >
              {MENU_LABELS.ARCHIVE}
            </MenuItem>

            <MenuItem
              icon={
                <span className={menuStyles.itemIconDanger}>
                  <TrashIcon size={16} />
                </span>
              }
              className={menuStyles.itemDanger}
              onClick={() => onDelete(workspace)}
            >
              {MENU_LABELS.DELETE}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  return (
    <Card
      className={mergeClasses(
        styles.container,
        isArchived && styles.cardArchived,
      )}
    >
      <div className={styles.cardInner}>
        <div className={cardDetailStyles.headerRow}>
          <div
            className={mergeClasses(
              cardDetailStyles.fixedTitleSection,
              styles.titleSection,
            )}
          >
            <div className={styles.title}>
              <Tooltip
                content={workspace.name}
                relationship="description"
                appearance="inverted"
                positioning="above"
                withArrow
              >
                <span className={styles.titleText} tabIndex={0}>
                  {workspace.name}
                </span>
              </Tooltip>
              {isPersonalWorkspace && (
                <Tooltip
                  content={TOOLTIP_MESSAGES.PERSONAL_WORKSPACE}
                  relationship="label"
                  appearance="inverted"
                  positioning="above"
                  withArrow
                >
                  <span className={styles.archivedLockIcon}>
                    <LockIcon size={16} />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          {renderContextMenu()}
        </div>

        <div className={styles.content}>
          <Tooltip
            content={
              workspace.clientName
                ? workspace.clientName.length >
                  WORKSPACE_CARD_CONSTANTS.TOOLTIP_MAX_CHARS
                  ? workspace.clientName.slice(
                      0,
                      WORKSPACE_CARD_CONSTANTS.TOOLTIP_MAX_CHARS,
                    ) + "..."
                  : workspace.clientName
                : "-"
            }
            relationship="description"
            appearance="inverted"
            positioning="above-start"
            withArrow
          >
            <span className={styles.descriptionText}>
              {workspace.clientName || "-"}
            </span>
          </Tooltip>
        </div>

        {!isPersonalWorkspace && (
          <div className={styles.iconRow}>
            <Tooltip
              content={membersList}
              relationship="description"
              appearance="inverted"
              positioning="below"
              withArrow
            >
              <Button
                appearance="subtle"
                icon={<UsersIcon />}
                aria-label="View members"
                size="small"
                className={styles.iconButton}
              />
            </Tooltip>

            <Tooltip
              content={
                workspace.libraryDocuments &&
                workspace.libraryDocuments.length > 0
                  ? workspace.libraryDocuments.map((doc, i) => (
                      <div key={i}>{doc.originalFilename}</div>
                    ))
                  : "No files uploaded"
              }
              relationship="description"
              appearance="inverted"
              positioning="below"
              withArrow
            >
              <Button
                appearance="subtle"
                icon={<DocumentIcon />}
                aria-label="View documents"
                size="small"
                className={styles.iconButton}
              />
            </Tooltip>

            <Tooltip
              content={TOOLTIP_MESSAGES.COPY_DIRECT_LINK}
              relationship="description"
              appearance="inverted"
              positioning="below"
              withArrow
            >
              <Button
                appearance="subtle"
                icon={<ShareRegular />}
                aria-label={TOOLTIP_MESSAGES.COPY_DIRECT_LINK}
                onClick={copyDirectLink}
                size="small"
                className={styles.iconButton}
              />
            </Tooltip>
          </div>
        )}

        <Tooltip
          content={TOOLTIP_MESSAGES.USE_WORKSPACE}
          relationship="description"
          appearance="inverted"
          positioning="above"
          withArrow
        >
          <Button
            size="small"
            appearance="subtle"
            icon={<OpenRegular />}
            onClick={handleUseWorkspace}
            disabled={isArchived}
            aria-label={TOOLTIP_MESSAGES.USE_WORKSPACE}
            className={styles.playButton}
          />
        </Tooltip>
      </div>
    </Card>
  );
};
